import {
  Button,
  Spacer,
  triggerToast,
  Modal,
  TextCapitalized,
  TableDS,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { GenerateBankStatementDisplayed } from "./statementApi";
import { unreachable } from "../../utils";
import {
  createBankStatementAsync,
  selectBankStatementCreationStatus,
  selectBankStatementsMultipleCreationStatus,
  bankStatementsListAsync,
  createBankStatementsMultipleAsync,
} from "./statementSlice";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import {
  companiesListAsync,
  selectCompaniesList,
  selectCompaniesListStatus,
} from "./../companiesSlice";
import { GetCompaniesApi } from "./../companiesApi";
import { useParams } from "react-router-dom";
import "../../i18n/i18n";

export const BankStatementCreationTab = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const params = useParams();
  const companiesList = useAppSelector(selectCompaniesList);
  const companiesListStatus = useAppSelector(selectCompaniesListStatus);
  const bankStatementCreationStatus = useAppSelector(
    selectBankStatementCreationStatus,
  );
  const bankStatementsMultipleCreationStatus = useAppSelector(
    selectBankStatementsMultipleCreationStatus,
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isMultipleGeneration, setIsMultipleGeneration] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<string>("");

  if (companiesListStatus === "idle") {
    void dispatch(companiesListAsync());
  }

  useEffect(() => {
    void dispatch(bankStatementsListAsync());
  }, [
    dispatch,
    bankStatementCreationStatus,
    bankStatementsMultipleCreationStatus,
  ]);

  useEffect(() => {
    if (bankStatementCreationStatus === "success") {
      triggerToast(t("bankStatement.creation.success") || "", "valid");
    } else if (bankStatementCreationStatus === "failed") {
      triggerToast(t("bankStatement.creation.failure") || "", "error");
    }
  }, [bankStatementCreationStatus, t, dispatch]);

  useEffect(() => {
    if (bankStatementsMultipleCreationStatus === "success") {
      triggerToast(t("bankStatement.multiple.creation.success") || "", "valid");
    } else if (bankStatementsMultipleCreationStatus === "failed") {
      triggerToast(t("bankStatement.multiple.creation.failure") || "", "error");
    }
  }, [bankStatementsMultipleCreationStatus, t, dispatch]);

  if (params.tab !== "bankStatementCreation") {
    return;
  }

  const renderGenerateStatement =
    (row: GenerateBankStatementDisplayed) =>
    (key: keyof GenerateBankStatementDisplayed) => {
      switch (key) {
        case "uuid":
          return (
            <StyledFlex>
              <Spacer x={1} />
              <Button
                title={t("bankStatement.button.generation")}
                width={12}
                onClick={() => {
                  setIsMultipleGeneration(false);
                  setCurrentCompany(row[key]);
                  setIsModalVisible(true);
                }}
              />
            </StyledFlex>
          );
        case "name":
          return row[key];
      }
      unreachable(key);
    };

  const headersGenerateStatements = {
    name: {
      text: t("bankStatement.page.column.company"),
    },
    uuid: {
      text: t("bankStatement.page.column.uuid"),
      hasButton: true,
    },
  };

  const retrieveCompanyUuid = (uuid: string) => {
    setCurrentCompany("");
    const company = companiesList.find(
      (row: GetCompaniesApi) => row.uuid === uuid,
    );
    if (!company) {
      triggerToast(t("statement.reloadCompany.error") || "", "error");
      return;
    }
    const date = new Date();
    const previousMonthDate = new Date(date.setMonth(date.getMonth() - 1));
    const datas = {
      company: uuid,
      date: previousMonthDate,
    };
    void dispatch(createBankStatementAsync(datas));
  };

  const ConfirmationModal = (props: { isMultipleGeneration: boolean }) => {
    return (
      <>
        <StyledDiv>
          <StyledContent>
            <TextCapitalized>
              {props.isMultipleGeneration === true
                ? t("bankStatement.modal.content.multiple")
                : t("bankStatement.modal.content", { account: currentCompany })}
            </TextCapitalized>
          </StyledContent>
        </StyledDiv>
        <Spacer y={4} />
        <StyledButtons>
          <Button
            title={t("bankStatement.modal.button.cancel")}
            width={12}
            onClick={() => {
              setIsModalVisible(false);
            }}
          />
          <Spacer x={2} />
          {props.isMultipleGeneration === true ? (
            <Button
              title={t("bankStatement.modal.button.confirm")}
              width={12}
              onClick={() => {
                void dispatch(createBankStatementsMultipleAsync());
                setIsModalVisible(false);
              }}
            />
          ) : (
            <Button
              title={t("bankStatement.modal.button.confirm")}
              width={12}
              onClick={() => {
                if (currentCompany !== "") retrieveCompanyUuid(currentCompany);
                setIsModalVisible(false);
              }}
            />
          )}
        </StyledButtons>
        <Spacer y={2} />
      </>
    );
  };

  return (
    <>
      <Modal
        title={t("bankStatement.modal.title") || ""}
        visible={isModalVisible}
        onClose={() => {
          setIsModalVisible(false);
        }}
        width={32}
      >
        <ConfirmationModal isMultipleGeneration={isMultipleGeneration} />
      </Modal>
      {/* <div>
        <Button
          title={t("bankStatement.button.generation.multiple")}
          width={12}
          onClick={() => {
            setIsMultipleGeneration(true);
            setIsModalVisible(true);
          }}
        />
      </div>
      <Spacer y={2} /> */}
      <TableDS<
        keyof GenerateBankStatementDisplayed,
        GenerateBankStatementDisplayed
      >
        data={companiesList}
        headers={headersGenerateStatements}
        render={renderGenerateStatement}
      />
    </>
  );
};

const StyledDiv = styled.div`
  display: flex;
  text-align: center;
`;

const StyledContent = styled.div`
  font-weight: 600;
  font-size: xx-large;
  color: ${colors["colors/system/error/error_normal"]};
`;

const StyledButtons = styled.div`
  display: flex;
  text-align: center;
  justify-content: center;
`;

const StyledFlex = styled.div`
  display: flex;
  align-items: center;
`;
